import { createCustomAction } from 'typesafe-actions';

import { CanvasPcodeVideoFrameParams } from '../../service/Pcode/PcodeCanvasVideo';
import { ValidationErrorData } from '../../typings/common';
import { Order, ResourceType } from '../common/typings';

import {
    FetchGalleryCategoriesResult,
    GalleriesRequestData,
    GalleryCollectionItem,
    GalleryDataForPane,
    GalleryPaneTypes,
    PreviewAudioItemParams,
    PreviewImageItemParams,
    PreviewVideoItemParams
} from './typings';

export const changeEnabledGalleryTypes = createCustomAction('@canvasGallery/CHANGE_ENABLED_TYPES', type => {
    return (resource: ResourceType, enabledGalleryTypes: GalleryPaneTypes[]) => ({
        type,
        resource,
        enabledGalleryTypes
    });
});

export const hoverItem = createCustomAction('@canvasGallery/HOVER_ITEM', type => {
    return (itemIndex?: number) => ({
        type,
        itemIndex
    });
});

export const apiActionError = createCustomAction('@canvasGallery/API_ACTION_ERROR', type => {
    return (message: string) => ({ type, message });
});

export const closeGalleryPopup = createCustomAction('@canvasGallery/CLOSE_POPUP', type => {
    return () => ({ type });
});

export const openGalleryPopup = createCustomAction('@canvasGallery/OPEN_POPUP', type => {
    return (
        resourceType: ResourceType,
        paneType: GalleryPaneTypes,
        fileEditorId: string,
        fileEnabledTypes?: GalleryPaneTypes[]
    ) => ({
        type,
        resourceType,
        paneType,
        fileEditorId,
        fileEnabledTypes
    });
});

export const fetchGalleryFiles = createCustomAction(
    '@canvasGallery/FETCH_GALLERY_FILES',
    type => (resourceType: ResourceType, paneType: GalleryPaneTypes, requestData: GalleriesRequestData) => ({
        type,
        resourceType,
        paneType,
        requestData
    })
);

export const fetchInitialData = createCustomAction('@canvasGallery/FETCH_INITIAL_DATA', type => {
    return (resourceType: ResourceType) => ({ type, resourceType });
});

export const reloadRecentCollection = createCustomAction('@canvasGallery/RELOAD_RECENT_COLLECTION', type => {
    return (resourceType: ResourceType) => ({ type, resourceType });
});

export const removeGalleryItemSuccess = createCustomAction('@canvasGallery/REMOVE_GALLERY_ITEM_SUCCESS', type => {
    return (resourceType: ResourceType, paneType: GalleryPaneTypes, itemIndex: number) => ({
        type,
        resourceType,
        paneType,
        itemIndex
    });
});

export const fetchItemsSuccess = createCustomAction('@canvasGallery/FETCH_ITEMS_SUCCESS', type => {
    return (
        resourceType: ResourceType,
        paneType: GalleryPaneTypes,
        data: GalleryDataForPane<GalleryCollectionItem>
    ) => ({
        type,
        resourceType,
        paneType,
        data
    });
});

export const previewImageItem = createCustomAction('@canvasGallery/PREVIEW_IMAGE_ITEM', type => {
    return (paneType: GalleryPaneTypes, params?: PreviewImageItemParams) => ({
        type,
        paneType,
        params
    });
});

export const previewAudioItem = createCustomAction('@canvasGallery/PREVIEW_AUDIO_ITEM', type => {
    return (paneType: GalleryPaneTypes, params?: PreviewAudioItemParams) => ({
        type,
        paneType,
        params
    });
});

export const previewVideoItem = createCustomAction('@canvasGallery/PREVIEW_VIDEO_ITEM', type => {
    return (paneType: GalleryPaneTypes, params?: PreviewVideoItemParams) => ({
        type,
        paneType,
        params
    });
});

export const removeItem = createCustomAction('@canvasGallery/REMOVE_ITEM', type => {
    return (itemCollectionIndex: number, itemName?: string) => ({
        type,
        itemCollectionIndex,
        itemName
    });
});

export const applyItem = createCustomAction('@canvasGallery/APPLY_ITEM', type => {
    return (itemCollectionIndex: number) => ({
        type,
        itemCollectionIndex
    });
});

export const editItemName = createCustomAction('@canvasGallery/EDIT_ITEM_NAME', type => {
    return (itemCollectionIndex: number, name: string) => ({
        type,
        name,
        itemCollectionIndex
    });
});

export const editItemNameError = createCustomAction('@canvasGallery/EDIT_ITEM_NAME_ERROR', type => {
    return (message: string) => ({
        type,
        message
    });
});

export const changeRecentNameFilter = createCustomAction('@canvasGallery/CHANGE_RECENT_NAME_FILTER', type => {
    return (recentNameFilter: string) => ({ type, recentNameFilter });
});

export const changeSemanticTextFilter = createCustomAction('@canvasGallery/CHANGE_SEMANTIC_TEXT_FILTER', type => {
    return (semanticText: string) => ({ type, semanticText });
});

export const changeRecentOrder = createCustomAction('@canvasGallery/CHANGE_RECENT_ORDER', type => {
    return (recentOrder: Order) => ({ type, recentOrder });
});

export const loadMoreItems = createCustomAction('@canvasGallery/LOAD_MORE_ITEMS', type => {
    return (limit: number) => ({ type, limit });
});

export const fetchGalleryCategoriesData = createCustomAction('@canvasGallery/FETCH_CATEGORIES_DATA', type => {
    return () => ({ type });
});

export const categoriesDataReceived = createCustomAction('@canvasGallery/CATEGORIES_DATA_RECEIVED', type => {
    return (fetchResult: FetchGalleryCategoriesResult) => ({
        type,
        fetchResult
    });
});

export const videoPreviewDataReceived = createCustomAction('@canvasGallery/VIDEO_PREVIEW_DATA_RECEIVED', type => {
    return (modalPopupItemSource: Partial<CanvasPcodeVideoFrameParams>) => ({
        type,
        modalPopupItemSource
    });
});

export const loadMoreItemsSuccess = createCustomAction('@canvasGallery/LOAD_MORE_ITEMS_SUCCESS', type => {
    return (
        resourceType: ResourceType,
        paneType: GalleryPaneTypes,
        data: GalleryDataForPane<GalleryCollectionItem>
    ) => ({
        type,
        resourceType,
        paneType,
        data
    });
});

export const changeSelectedGalleryCategories = createCustomAction('@canvasGallery/CHANGE_SELECTED_CATEGORIES', type => {
    return (selectedGalleryCategories: string[]) => ({
        type,
        selectedGalleryCategories
    });
});

export const selectTab = createCustomAction('@canvasGallery/SELECT_TAB', type => {
    return (currentPaneType: GalleryPaneTypes) => ({
        type,
        currentPaneType
    });
});

export const previewError = createCustomAction('@canvasGallery/PREVIEW_ERROR', type => {
    return (error: ValidationErrorData) => ({
        type,
        error
    });
});

export type SelectTab = ReturnType<typeof selectTab>;
export type HoverItem = ReturnType<typeof hoverItem>;
export type PreviewVideoItem = ReturnType<typeof previewVideoItem>;
export type PreviewAudioItem = ReturnType<typeof previewAudioItem>;
export type PreviewImageItem = ReturnType<typeof previewImageItem>;
export type ChangeRecentNameFilter = ReturnType<typeof changeRecentNameFilter>;
export type ChangeRecentOrder = ReturnType<typeof changeRecentOrder>;
export type LoadMoreItems = ReturnType<typeof loadMoreItems>;
export type EditItemName = ReturnType<typeof editItemName>;
export type EditItemNameError = ReturnType<typeof editItemNameError>;
export type RemoveItem = ReturnType<typeof removeItem>;
export type ApplyItem = ReturnType<typeof applyItem>;
export type CloseGalleryPopup = ReturnType<typeof closeGalleryPopup>;
export type OpenGalleryPopup = ReturnType<typeof openGalleryPopup>;

export type ApiActionError = ReturnType<typeof apiActionError>;
export type VideoPreviewDataReceived = ReturnType<typeof videoPreviewDataReceived>;
export type ChangeSemanticTextFilter = ReturnType<typeof changeSemanticTextFilter>;
export type FetchGalleryCategoriesData = ReturnType<typeof fetchGalleryCategoriesData>;
export type CategoriesDataReceived = ReturnType<typeof categoriesDataReceived>;
export type ChangeSelectedGalleryCategories = ReturnType<typeof changeSelectedGalleryCategories>;
export type FetchGalleryFiles = ReturnType<typeof fetchGalleryFiles>;
export type FetchInitialData = ReturnType<typeof fetchInitialData>;
export type ReloadRecentCollection = ReturnType<typeof reloadRecentCollection>;

export type GalleryAction =
    | SelectTab
    | HoverItem
    | PreviewVideoItem
    | PreviewAudioItem
    | PreviewImageItem
    | ChangeRecentNameFilter
    | ChangeRecentOrder
    | LoadMoreItems
    | FetchGalleryCategoriesData
    | CategoriesDataReceived
    | ChangeSelectedGalleryCategories
    | EditItemNameError
    | EditItemName
    | RemoveItem
    | ApplyItem
    | CloseGalleryPopup
    | OpenGalleryPopup
    | ApiActionError
    | ChangeSemanticTextFilter
    | VideoPreviewDataReceived
    | ReturnType<typeof fetchItemsSuccess>
    | ReturnType<typeof removeGalleryItemSuccess>
    | ReturnType<typeof loadMoreItemsSuccess>
    | ReturnType<typeof changeEnabledGalleryTypes>
    | ReturnType<typeof previewError>
    | FetchInitialData
    | ReloadRecentCollection
    | FetchGalleryFiles;
